import React, { useContext } from 'react';
import { ActiveFilterType, GridFilterModel, OptionType } from '~/types/AgGridTypes';
import { FilterState, ResolveActiveFilterParams, ResolvedFilterState, useFilterState } from '../hooks/useFilterState';

interface FilterStateContextProps {
  filterState: FilterState;
  updateFilterValue: (value: OptionType | null) => void;
  updateExplicitlyClearedFilter: (cleared: boolean) => void;
  resolveActiveFilter: (params: ResolveActiveFilterParams) => ResolvedFilterState;
  updateTableState: (tableState: string) => void;
  updateActiveFilters: (activeFilters: ActiveFilterType | null) => void;
  getFilterState: () => FilterState;
  updatePresetFilter: (isPresetFilterChip: boolean) => void;
  updateSavedFilter: (saved: boolean) => void;
  updateSearchText: (searchText: string) => void;
  updateFilterColumn: (filterColumn: GridFilterModel) => void;
}

const FilterStateContext = React.createContext<FilterStateContextProps | undefined>(undefined);
export type FilterContextValue = ReturnType<typeof useFilterState>;

export const FilterStateContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const {
    filterState,
    updateFilterValue,
    updateExplicitlyClearedFilter,
    resolveActiveFilter,
    updateTableState,
    updateActiveFilters,
    getFilterState,
    updatePresetFilter,
    updateSavedFilter,
    updateSearchText,
    updateFilterColumn,
  } = useFilterState();

  const contextValue = React.useMemo(
    () => ({
      filterState,
      updateFilterValue,
      updateExplicitlyClearedFilter,
      resolveActiveFilter,
      updateTableState,
      updateActiveFilters,
      getFilterState,
      updatePresetFilter,
      updateSavedFilter,
      updateSearchText,
      updateFilterColumn,
    }),
    [
      filterState,
      updateFilterValue,
      updateExplicitlyClearedFilter,
      resolveActiveFilter,
      updateTableState,
      updateActiveFilters,
      getFilterState,
      updatePresetFilter,
      updateSavedFilter,
      updateSearchText,
      updateFilterColumn,
    ],
  );

  return <FilterStateContext.Provider value={contextValue}>{children}</FilterStateContext.Provider>;
};

export const useFilterStateContext = () => {
  const context = useContext(FilterStateContext);
  if (context === undefined) {
    throw new Error('useFilterStateContext must be used within a FilterStateContextProvider');
  }
  return context;
};
